import { yupResolver } from '@hookform/resolvers/yup';
import ContentPasteGoOutlinedIcon from '@mui/icons-material/ContentPasteGoOutlined';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import clsx from 'clsx';
import { FC, FocusEvent, useEffect, useState } from 'react';
import { Controller, SubmitHandler, useForm, useWatch } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { setNotice } from 'src/redux/noticesSlice';
import * as yup from 'yup';
import { handlePasteToForm, isObjectEmpty } from '../../../../helpers';
import {
  ILdapParams,
  IProvider,
  useCreateProviderMutation,
  useUpdateAvatarMutation,
} from '../../../../redux/services/provider';
import { ProviderHeader } from '../../../Providers/ProviderHeader';
import { CustomTypography } from '../../../custom/CustomTypography';
import { PasswordTextfield } from '../../../custom/PasswordTextfield';
import { ConfirmationModal } from '../../../modal/ConfirmationModal';
import { SharedFormProvider } from '../../../shared/FormProvider';
import { SidePanel } from '../../../sidePanel/SidePanel';
import { addProviderType } from '../../Settings';
import styles from './CreateProvider.module.css';
import { ENoticeType } from 'src/enums';

const schema = yup.object({
  name: yup
    .string()
    .required('Обязательное поле')
    .max(50, 'Название не может превышать 50 символов')
    .matches(/[^ ]+/, {
      message: 'Название не может состоять только из пробелов',
    })
    .matches(/^[^ ]+( *[^ ]+)*?$/, 'Название не может содержать пробелы в начале и конце'),
  description: yup
    .string()
    .max(255, 'Описание не может превышать 255 символов')
    .matches(/^$|[^ ]+/, {
      message: 'Описание не может состоять только из пробелов',
    }),
  password_required: yup.boolean(),
  is_public: yup.boolean(),
  params: yup.object({
    url: yup
      .string()
      .required('Обязательное поле')
      .max(255, 'Адрес сервера не может превышать 255 символов')
      .matches(/^[^\n ]*$/, {
        message: 'Адрес не может содержать пробелы',
      })
      .matches(/ldaps?:\/\//, {
        excludeEmptyString: true,
        message: 'Адрес должен начинаться с ldap:// или ldaps://',
      }),
    base: yup
      .string()
      .required('Обязательное поле')
      .max(255, 'База поиска не может превышать 255 символов'),
    domain: yup
      .string()
      .required('Обязательное поле')
      .max(255, 'Имя домена не может превышать 255 символов')
      .matches(
        /^[a-z0-9.-]+$/,
        'Имя может содержать только буквы (a-z), числовые символы (0-9), знак минуса (-) и точку (.)',
      ),
    mapping: yup.string().max(1024, 'Поле не может превышать 1024 символов'),
    admin_login: yup.string().required('Обязательное поле'),
    admin_password: yup
      .string()
      .min(6, 'Пароль должен быть минимум 6 символов')
      .max(255, 'Пароль не может превышать 255 символов'),
    disable_password_reset: yup.boolean(),
  }),
});

interface ICreateProviderProps {
  isOpen: boolean;
  onClose: (createChooseProvider?: boolean) => void;
  defaultAvatar: string;
  providerType: addProviderType.LDAP | addProviderType.ALDPRO;
}

export const CreateLdapProvider: FC<ICreateProviderProps> = ({
  isOpen,
  onClose,
  defaultAvatar,
  providerType,
}) => {
  const { appId = '', clientId = '' } = useParams<{ appId: string; clientId: string }>();
  const dispatch = useDispatch();
  const methods = useForm<IProvider<ILdapParams>>({
    resolver: yupResolver(schema),
    defaultValues: {
      name: providerType,
      avatar: defaultAvatar,
      type: providerType,
    },
    mode: 'onBlur',
    reValidateMode: 'onBlur',
  });

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors, dirtyFields },
    clearErrors,
    reset,
    control,
  } = methods;

  const [saveModalOpen, setSaveModalOpen] = useState(false);
  const [createProvider, createResult] = useCreateProviderMutation();
  const [updateAvatar] = useUpdateAvatarMutation();

  const uploadedAvatar = useWatch({ control, name: 'avatar' });

  useEffect(() => {
    if (createResult.isSuccess) onClose(true);
  }, [createResult]);

  useEffect(() => {
    return () => {
      reset();
    };
  }, [isOpen]);

  const closeSaveModal = () => setSaveModalOpen(false);

  const handleClose = () => {
    if (isObjectEmpty(dirtyFields)) onClose();
    else setSaveModalOpen(true);
  };

  const pasteFields = async () => {
    try {
      await handlePasteToForm(methods);
    } catch (err) {
      dispatch(
        setNotice({
          type: ENoticeType.error,
          id: Math.random(),
          isRead: false,
          message: `Ошибка вставки параметров: ${err}`,
          timestamp: new Date().toString(),
        }),
      );
    }
  };

  const onSubmit: SubmitHandler<IProvider<ILdapParams>> = (data) => {
    createProvider({
      body: data,
      clientId: clientId || appId,
    })
      .unwrap()
      .then((provider) => {
        if (provider.id) {
          setTimeout(() => {
            updateAvatar({
              clientId: clientId || appId,
              providerId: provider.id,
              avatar: uploadedAvatar,
            }).unwrap();
          }, 1000);
        }
      });
  };

  return (
    <SidePanel
      onClose={handleClose}
      isOpen={isOpen}
      title={`Создать провайдер ${providerType}`}
      AdditionalAction={() => pasteFields()}
      customAdditionalText="Вставить настройки"
      CustomAdditionalIcon={ContentPasteGoOutlinedIcon}
      isNoBackdrop
    >
      <SharedFormProvider<IProvider<ILdapParams>>
        methods={methods}
        onSubmit={handleSubmit(onSubmit)}
        handleCancel={handleClose}
        isDisabled={createResult.isLoading}
        acceptTitle="Создать"
      >
        <ProviderHeader defaultAvatar={defaultAvatar} />
        <CustomTypography className={clsx('text-14', styles.asterisk, styles['input-title'])}>
          Адрес сервера {providerType} (ldap_url)
        </CustomTypography>
        <TextField
          {...register('params.url', {
            required: true,
            onBlur: (event: FocusEvent<HTMLInputElement>) => {
              setValue('params.url', event.target.value.trim());
            },
            onChange: () => {
              if (errors.params?.url) clearErrors('params.url');
            },
          })}
          className="custom"
          error={!!errors.params?.url}
          helperText={errors.params?.url?.message}
          fullWidth
          variant="standard"
        />
        <CustomTypography className={clsx('text-14', styles['input-subtitle'])} color="grey">
          Адрес сервера {providerType} в формате ldaps://example.com
        </CustomTypography>
        <CustomTypography className={clsx('text-14', styles.asterisk, styles['input-title'])}>
          База поиска (ldap_base)
        </CustomTypography>
        <TextField
          {...register('params.base', {
            required: true,
            onBlur: (event: FocusEvent<HTMLInputElement>) => {
              setValue('params.base', event.target.value.trim());
            },
            onChange: () => {
              if (errors.params?.base) clearErrors('params.base');
            },
          })}
          className="custom"
          error={!!errors.params?.base}
          helperText={errors.params?.base?.message}
          fullWidth
          variant="standard"
        />
        <CustomTypography className={clsx('text-14', styles['input-subtitle'])} color="grey">
          Объект каталога, начиная с которого будет производиться поиск
        </CustomTypography>
        <CustomTypography className={clsx('text-14', styles.asterisk, styles['input-title'])}>
          Домен {providerType} (ldap_domain)
        </CustomTypography>
        <TextField
          {...register('params.domain', {
            required: true,
            onChange: () => {
              if (errors.params?.domain) clearErrors('params.domain');
            },
          })}
          className="custom"
          error={!!errors.params?.domain}
          helperText={errors.params?.domain?.message}
          fullWidth
          variant="standard"
        />
        <CustomTypography className={clsx('text-14', styles['input-subtitle'])} color="grey">
          Имя домена, которому принадлежат пользователи
        </CustomTypography>
        <CustomTypography className={clsx('text-14', styles['input-title'])}>
          Фильтр поиска (ldap_filter)
        </CustomTypography>
        <TextField
          {...register('params.search_filter', {
            onChange: () => {
              if (errors.params?.search_filter) clearErrors('params.search_filter');
            },
          })}
          className="custom"
          error={!!errors.params?.search_filter}
          helperText={errors.params?.search_filter?.message}
          fullWidth
          variant="standard"
        />
        <CustomTypography className={clsx('text-14', styles['input-subtitle'])} color="grey">
          Фильтр для поиска учетной записи пользователя
        </CustomTypography>
        <CustomTypography className={clsx('text-14', styles['input-title'])}>
          Сопоставление атрибутов {providerType} (ldap_mapping)
        </CustomTypography>
        <TextField
          {...register('params.mapping', {
            required: true,
            onChange: () => {
              if (errors.params?.mapping) clearErrors('params.mapping');
            },
          })}
          className="custom"
          error={!!errors.params?.mapping}
          helperText={errors.params?.mapping?.message}
          fullWidth
          variant="standard"
        />
        <CustomTypography className={clsx('text-14', styles['input-subtitle'])} color="grey">
          Сопоставление атрибутов профиля пользователя Trusted.ID с атрибутами пользователя внешней
          системы в формате: given_name:givenName, family_name:sn, email:mail, picture:photo
        </CustomTypography>
        <CustomTypography className={clsx('text-14', styles.asterisk, styles['input-title'])}>
          Логин администратора (ldap_admin_dn)
        </CustomTypography>
        <TextField
          {...register('params.admin_login', {
            required: true,
            onChange: () => {
              if (errors.params?.admin_login) clearErrors('params.admin_login');
            },
          })}
          className="custom"
          error={!!errors.params?.admin_login}
          helperText={errors.params?.admin_login?.message}
          fullWidth
          variant="standard"
        />
        <CustomTypography className={clsx('text-14', styles['input-subtitle'])} color="grey">
          Логин администратора {providerType}
        </CustomTypography>
        <CustomTypography className={clsx('text-14', styles.asterisk, styles['input-title'])}>
          Пароль администратора (ldap_admin_pwd)
        </CustomTypography>
        <PasswordTextfield nameField="params.admin_password" />
        <CustomTypography className={clsx('text-14', styles['input-subtitle'])} color="grey">
          Пароль администратора {providerType}
        </CustomTypography>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <div>
            <CustomTypography className={clsx('text-14', styles['input-title'])}>
              Запретить сброс пароля
            </CustomTypography>
            <CustomTypography className={clsx('text-14', styles['input-subtitle'])} color="grey">
              Пользователь не сможет восстановить пароль от учетной записи для данного способа входа
            </CustomTypography>
          </div>
          <Controller
            name="params.disable_password_reset"
            control={control}
            defaultValue={false}
            render={({ field }) => (
              <Switch
                classes={{ root: styles['switch-root'] }}
                disableRipple
                checked={field.value}
                onChange={(e) => field.onChange(e.target.checked)}
              />
            )}
          />
        </div>
      </SharedFormProvider>

      <ConfirmationModal
        title="Сохранение изменений"
        mainMessage={['Изменения не сохранены. Продолжить без сохранения?']}
        actionButtonText="Продолжить"
        isOpen={saveModalOpen}
        onAction={() => {
          onClose();
          setSaveModalOpen(false);
        }}
        onClose={closeSaveModal}
      />
    </SidePanel>
  );
};
